import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { TeamPhoto } from './TeamPhoto';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { insuranceProducts, investmentProducts } from '../mock';
import { useLanguage } from '../context/LanguageContext';
import { useAgent } from '../context/AgentContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const { agent, isAgentPage, buildLink, getContactInfo } = useAgent();

  // Hide header on dedicated pages (e-signature signing flow, client portal intake, etc.)
  if (location.pathname.startsWith('/sign/') || location.pathname.startsWith('/intake/')) {
    return null;
  }

  const isActive = (path) => location.pathname === path;
  const contactInfo = getContactInfo();

  // Helper to build links that respect agent context
  const linkTo = (path) => {
    if (isAgentPage && agent) {
      return buildLink(path);
    }
    return path;
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      {/* Agent Banner - shows when on agent page */}
      {isAgentPage && agent && (
        <div className="bg-[#003D5C] py-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-[#D4AF37]">
                  <TeamPhoto src={agent.photo} name={agent.name} className="w-full h-full object-cover" />
                </div>
                <span className="font-semibold">{agent.name}</span>
                <span className="text-blue-200 text-sm hidden sm:inline">| {
                  agent.licenses?.includes('mortgage') && (agent.licenses?.includes('insurance') || agent.licenses?.includes('investments'))
                    ? 'Your Mortgage & Financial Advisor'
                    : agent.licenses?.includes('mortgage')
                      ? 'Your Mortgage Professional'
                      : 'Your Financial Advisor'
                }</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <a href={`tel:${agent.phone}`} className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors">
                  <Phone className="h-3.5 w-3.5" />
                  <span>{agent.phone}</span>
                </a>
                <a href={`mailto:${agent.email}`} className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{agent.email}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo - Always links to main homepage */}
          <Link to="/" className="flex items-center">
            <img 
              src="/utmost-logo.png" 
              alt="Utmost Financial Inc." 
              className="h-16 md:h-20 lg:h-24 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <NavigationMenu>
              <NavigationMenuList>
                {/* Simple Home link instead of dropdown */}
                <NavigationMenuItem>
                  <Link
                    to={linkTo('/')}
                    className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-[#0077B6] hover:bg-[#F0F7FF] transition-colors"
                  >
                    Home
                  </Link>
                </NavigationMenuItem>

                {/* Insurance Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-gray-700 hover:text-[#0077B6] bg-transparent font-medium">
                    Insurance
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] p-4 bg-white border border-gray-200 shadow-lg rounded-lg">
                      <div className="grid gap-2">
                        {insuranceProducts.map((product) => (
                          <Link
                            key={product.id}
                            to={linkTo('/insurance')}
                            className="block p-3 rounded-md bg-[#F0F7FF] hover:bg-[#0077B6] hover:text-white transition-colors border border-blue-100"
                          >
                            <div className="font-bold text-base text-gray-800">{product.title}</div>
                            <div className="text-sm text-gray-600 mt-1">{product.description}</div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Investments Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-gray-700 hover:text-[#0077B6] bg-transparent font-medium">
                    Investments
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[400px] p-4 bg-white border border-gray-200 shadow-lg rounded-lg">
                      <div className="grid gap-2">
                        {investmentProducts.map((product) => (
                          <Link
                            key={product.id}
                            to={linkTo('/investment')}
                            className="block p-3 rounded-md bg-[#F0F7FF] hover:bg-[#0077B6] hover:text-white transition-colors border border-blue-100"
                          >
                            <div className="font-bold text-base text-gray-800">{product.title}</div>
                            <div className="text-sm text-gray-600 mt-1">{product.description}</div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to={linkTo('/mortgage')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/mortgage') ? 'text-[#0077B6] bg-[#F0F7FF]' : 'text-gray-700 hover:text-[#0077B6] hover:bg-[#F0F7FF]'
              }`}
            >
              Mortgage
            </Link>
            <Link
              to={linkTo('/team')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/team') ? 'text-[#0077B6] bg-[#F0F7FF]' : 'text-gray-700 hover:text-[#0077B6] hover:bg-[#F0F7FF]'
              }`}
            >
              Our Team
            </Link>
            <Link
              to={linkTo('/partners')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/partners') ? 'text-[#0077B6] bg-[#F0F7FF]' : 'text-gray-700 hover:text-[#0077B6] hover:bg-[#F0F7FF]'
              }`}
            >
              Partners
            </Link>
            <Link
              to={linkTo('/resources')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/resources') ? 'text-[#0077B6] bg-[#F0F7FF]' : 'text-gray-700 hover:text-[#0077B6] hover:bg-[#F0F7FF]'
              }`}
            >
              Resources
            </Link>
          </nav>

          {/* Header Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-[#0077B6] hover:bg-[#F0F7FF] transition-colors"
              title={language === 'en' ? 'Switch to French' : 'Passer à l\'anglais'}
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4" />
              <span className="font-semibold">{language === 'en' ? 'FR' : 'EN'}</span>
            </button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#003D5C] font-semibold"
            >
              <Link to="/referrer-login">
                Referral
              </Link>
            </Button>
            <Button
              asChild
              className="bg-[#0077B6] hover:bg-[#005A8C] text-white font-semibold"
            >
              <Link to="/agent-login">
                Agent Login
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 p-2 hover:bg-[#F0F7FF] rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 bg-white">
            <div className="flex flex-col space-y-1">
              {/* Simple Home link */}
              <Link
                to={linkTo('/')}
                className="px-3 py-3 rounded-md text-lg font-bold text-gray-800 hover:bg-[#F0F7FF] border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>

              {/* Insurance with submenu */}
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => {
                    const submenu = document.getElementById('mobile-insurance-submenu');
                    submenu.classList.toggle('hidden');
                  }}
                  className="w-full flex justify-between items-center px-3 py-3 rounded-md text-lg font-bold text-gray-800 hover:bg-[#F0F7FF]"
                >
                  <span>Insurance</span>
                  <ChevronDown size={20} className="text-[#0077B6]" />
                </button>
                <div id="mobile-insurance-submenu" className="hidden pl-4 mt-1 space-y-1 bg-[#F0F7FF] rounded-md py-2">
                  {insuranceProducts.map((product) => (
                    <Link
                      key={product.id}
                      to={linkTo('/insurance')}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-[#0077B6] hover:text-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {product.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Investments with submenu */}
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => {
                    const submenu = document.getElementById('mobile-investment-submenu');
                    submenu.classList.toggle('hidden');
                  }}
                  className="w-full flex justify-between items-center px-3 py-3 rounded-md text-lg font-bold text-gray-800 hover:bg-[#F0F7FF]"
                >
                  <span>Investments</span>
                  <ChevronDown size={20} className="text-[#0077B6]" />
                </button>
                <div id="mobile-investment-submenu" className="hidden pl-4 mt-1 space-y-1 bg-[#F0F7FF] rounded-md py-2">
                  {investmentProducts.map((product) => (
                    <Link
                      key={product.id}
                      to={linkTo('/investment')}
                      className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-[#0077B6] hover:text-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {product.title}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to={linkTo('/mortgage')}
                className="px-3 py-3 rounded-md text-lg font-bold text-gray-800 hover:bg-[#F0F7FF] border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Mortgage
              </Link>
              <Link
                to={linkTo('/team')}
                className="px-3 py-3 rounded-md text-lg font-bold text-gray-800 hover:bg-[#F0F7FF] border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Our Team
              </Link>
              <Link
                to={linkTo('/partners')}
                className="px-3 py-3 rounded-md text-lg font-bold text-gray-800 hover:bg-[#F0F7FF] border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Partners
              </Link>
              <Link
                to={linkTo('/resources')}
                className="px-3 py-3 rounded-md text-lg font-bold text-gray-800 hover:bg-[#F0F7FF] border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                Resources
              </Link>
              <div className="flex flex-col space-y-3 mt-4 px-3">
                {/* Language Toggle for Mobile */}
                <button
                  onClick={toggleLanguage}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-md text-base font-bold text-gray-700 bg-[#F0F7FF] hover:bg-[#0077B6] hover:text-white transition-colors"
                  data-testid="mobile-language-toggle"
                >
                  <Globe className="h-5 w-5" />
                  <span>{language === 'en' ? 'Français' : 'English'}</span>
                </button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#003D5C] font-bold text-base py-3"
                >
                  <Link to="/referrer-login" onClick={() => setMobileMenuOpen(false)}>
                    Referral
                  </Link>
                </Button>
                <Button
                  asChild
                  className="bg-[#0077B6] hover:bg-[#005A8C] text-white font-bold text-base py-3"
                >
                  <Link to="/agent-login" onClick={() => setMobileMenuOpen(false)}>
                    Agent Login
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
